import React, { forwardRef } from "react";
import DiamondShapeSVG from "../DiamondShapeSVG";

const fmtVal = (v) =>
  typeof v === "boolean" ? (v ? "Yes" : "No") : v === null || v === undefined ? "—" : String(v);

const GeoSplash = forwardRef(({
  product,
  buyer,
  cardScale = 100,
  productImageRef,
  visibleFields = {},
  cssVars = {},
  forExport = false,
}, ref) => {
  const isVisible = (key) => visibleFields[key] !== false;

  const price = product?.pricing?.retail || product?.attributes?.retail_price || 0;
  const formatPrice = (v) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency", currency: "INR",
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(v);

  const segmentAttrEntries = Object.entries(product?.attributes || {})
    .filter(([k]) => k !== "retail_price")
    .filter(([k]) => isVisible(k));

  const rootStyle = {
    transform: forExport ? "scale(1)" : `scale(${cardScale / 100})`,
    transformOrigin: "center top",
    width: "420px",
    height: "560px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    ...cssVars,
  };

  return (
    <div ref={ref} style={rootStyle} className="card-preview-wrapper layout-geo-splash">

      {/* ── Background ── */}
      <div style={{ position: "absolute", inset: 0, background: "var(--theme-bg)", zIndex: 0 }} />

      {/* ── Bold geometric SVG shapes ── */}
      <svg style={{ position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none" }}
        width="420" height="560" viewBox="0 0 420 560">

        {/* Large accent hexagon top-right */}
        <polygon
          points="340,0 420,46 420,138 340,184 260,138 260,46"
          fill="var(--theme-accent)" opacity="0.12"
        />
        {/* Smaller hexagon bottom-left */}
        <polygon
          points="80,376 140,410 140,478 80,512 20,478 20,410"
          fill="var(--theme-accent)" opacity="0.09"
        />
        {/* Triangle top-left corner splash */}
        <polygon
          points="0,0 140,0 0,200"
          fill="var(--theme-accent)" opacity="0.08"
        />
        {/* Triangle bottom-right */}
        <polygon
          points="420,560 280,560 420,360"
          fill="var(--theme-accent)" opacity="0.08"
        />
        {/* Parallelogram band across middle */}
        <polygon
          points="0,240 420,200 420,212 0,252"
          fill="var(--theme-accent)" opacity="0.18"
        />
        {/* Outline hexagon (decorative) */}
        <polygon
          points="210,60 260,89 260,147 210,176 160,147 160,89"
          fill="none" stroke="var(--theme-accent)" strokeWidth="1"
          opacity="0.2"
        />
        {/* Small diamonds scattered */}
        {[
          [38, 38], [382, 38], [38, 522], [382, 522],
          [210, 30],
        ].map(([x, y], i) => (
          <polygon key={i}
            points={`${x},${y - 6} ${x + 6},${y} ${x},${y + 6} ${x - 6},${y}`}
            fill="var(--theme-accent)" opacity="0.45"
          />
        ))}
      </svg>

      {/* ── Thick left accent stripe ── */}
      <div style={{
        position: "absolute", top: 0, left: 0, width: 6, bottom: 0,
        background: "var(--theme-accent)", zIndex: 10,
      }} />

      {/* ── Content ── */}
      <div style={{
        position: "relative", zIndex: 5,
        display: "flex", flexDirection: "column",
        height: "100%", padding: "16px 20px 14px 20px",
      }}>

        {/* ── Brand bar ── */}
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", marginBottom: 8,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            {isVisible("logoUrl") && buyer?.logoUrl && (
              <img src={buyer.logoUrl} alt="Logo"
                style={{ width: 24, height: 24, objectFit: "contain", borderRadius: 2 }} />
            )}
            {isVisible("organizationName") && (
              <span style={{
                fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
                fontSize: "0.8rem", fontWeight: 900,
                color: "var(--theme-text)",
                textTransform: "uppercase", letterSpacing: "0.15em",
              }}>
                {(buyer?.organizationName || "GEO").toUpperCase()}
              </span>
            )}
          </div>
          {/* bold tag */}
          <div style={{
            background: "var(--theme-accent)", color: "var(--theme-bg)",
            fontSize: "0.52rem", fontWeight: 900,
            padding: "3px 10px",
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
            letterSpacing: "0.12em",
            clipPath: "polygon(0 0, 100% 0, calc(100% - 6px) 100%, 0 100%)",
          }}>
            {product?.segment?.toUpperCase() || "JEWELLERY"}
          </div>
        </div>

        {/* ── Big bold title block ── */}
        <div style={{ marginBottom: 6 }}>
          <div style={{
            fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
            fontSize: "0.55rem", fontWeight: 700, letterSpacing: "0.3em",
            color: "var(--theme-accent)", textTransform: "uppercase",
            marginBottom: 2,
          }}>
            FEATURED PIECE
          </div>
          <div style={{
            fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
            fontSize: "1.25rem", fontWeight: 900,
            color: "var(--theme-text)", lineHeight: 1.1,
            letterSpacing: "0.03em",
          }}>
            {product?.name || "Fine Jewellery"}
          </div>
        </div>

        {/* ── Product image — clipped in a tilted hexagonal mask ── */}
        <div style={{
          position: "relative",
          flex: "0 0 190px",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 8,
        }}>
          {/* hexagon background */}
          <svg style={{ position: "absolute", zIndex: 0 }}
            width="200" height="200" viewBox="0 0 200 200">
            <polygon
              points="100,4 188,52 188,148 100,196 12,148 12,52"
              fill="var(--theme-surface)" opacity="0.85"
            />
            <polygon
              points="100,4 188,52 188,148 100,196 12,148 12,52"
              fill="none" stroke="var(--theme-accent)" strokeWidth="1.5" opacity="0.4"
            />
          </svg>
          <img
            src={Array.isArray(product?.image_url) ? product.image_url[0] : product?.image_url}
            alt={product?.name || ""}
            crossOrigin="anonymous"
            style={{
              maxWidth: "65%", maxHeight: "80%", objectFit: "contain",
              position: "relative", zIndex: 2,
              filter: "drop-shadow(0 8px 22px rgba(0,0,0,0.38))",
            }}
          />
        </div>

        {/* ── Attributes ── */}
        {segmentAttrEntries.length > 0 && (
          <div style={{
            display: "grid", gridTemplateColumns: "1fr 1fr",
            gap: "3px 10px",
            background: "var(--theme-surface)",
            borderLeft: "4px solid var(--theme-accent)",
            borderRadius: 2, padding: "6px 10px", marginBottom: 8,
          }}>
            {segmentAttrEntries.map(([k, v]) => (
              <div key={k} style={{ display: "flex", flexDirection: "column" }}>
                <span style={{
                  fontSize: "0.47rem", fontWeight: 700,
                  color: "var(--theme-accent)",
                  textTransform: "uppercase", letterSpacing: "0.1em",
                  fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                }}>
                  {k.replace(/_/g, " ")}
                </span>
                {k === "shape" && product?.segment?.toLowerCase() === "diamond" ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <DiamondShapeSVG shapeName={String(v)} size={22} color="var(--theme-accent)" />
                    <span style={{
                      fontSize: "0.62rem", fontWeight: 600, color: "var(--theme-text)",
                      fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                    }}>{fmtVal(v)}</span>
                  </div>
                ) : (
                  <span style={{
                    fontSize: "0.64rem", fontWeight: 600, color: "var(--theme-text)",
                    fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
                  }}>{fmtVal(v)}</span>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ flex: 1 }} />

        {/* ── Price band (full-width accent block) ── */}
        <div style={{
          background: "var(--theme-accent)",
          padding: "10px 16px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginBottom: 7,
          clipPath: "polygon(0 0, 100% 0, calc(100% - 10px) 100%, 10px 100%)",
        }}>
          <div style={{
            fontFamily: "var(--theme-heading-font,'Montserrat',sans-serif)",
            fontSize: "1.15rem", fontWeight: 900,
            color: "var(--theme-bg)",
          }}>
            {formatPrice(price)}
          </div>
          <div style={{
            fontSize: "0.6rem", fontWeight: 800,
            color: "var(--theme-bg)", letterSpacing: "0.12em",
            fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          }}>
            ▶ ORDER NOW
          </div>
        </div>

        {/* ── Footer ── */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          borderTop: "1px solid var(--theme-border)", paddingTop: 5,
          fontSize: "0.5rem", color: "var(--theme-textMuted)",
          fontFamily: "var(--theme-body-font,'Open Sans',sans-serif)",
          letterSpacing: "0.06em",
        }}>
          {isVisible("address") && <span>{buyer?.streetAddress || "123 Jewellers Lane"}</span>}
          {isVisible("phone") && <span>{buyer?.transactionalMobile || buyer?.mobile || "+91 98765 43210"}</span>}
        </div>
      </div>
    </div>
  );
});

GeoSplash.displayName = "GeoSplash";
export default GeoSplash;
